import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { MasterControlHealth } from "@/hooks/useMasterControl";
import { cn } from "@/lib/utils";

type ServiceEntry = {
  key: string;
  label: string;
  statusLabel: string;
  tone: "ok" | "warn" | "fail";
  detail?: string | null;
};

function resolveServicePayload(health: MasterControlHealth, key: string): Record<string, unknown> | null {
  if (key === "accounting") {
    const ok = health.accounting?.ok ?? false;
    return {
      status: ok ? "healthy" : "issues",
      detail: `${Object.keys(health.accounting ?? {}).length} checks`,
    };
  }
  const services = (health.core?.services as Record<string, Record<string, unknown>> | undefined) ?? {};
  if (services[key]) return services[key];
  const fallback = (health.core as Record<string, Record<string, unknown>> | null) ?? null;
  return fallback?.[key] ?? null;
}

function resolveServiceEntry(health: MasterControlHealth, key: string, label: string): ServiceEntry {
  const payload = resolveServicePayload(health, key);
  const statusRaw = (payload?.status ?? payload?.state ?? "unknown").toString();
  const normalized = statusRaw.toLowerCase();
  const tone: ServiceEntry["tone"] = normalized.includes("error") || normalized.includes("fail")
    ? "fail"
    : normalized.includes("warn") || normalized.includes("degraded")
      ? "warn"
      : "ok";
  const latency = payload?.latency_ms ?? payload?.latency;
  let detail: string | null = null;
  if (typeof latency === "number" && Number.isFinite(latency)) {
    detail = `${latency.toFixed(latency >= 10 ? 0 : 1)} ms latency`;
  } else if (typeof payload?.uptime === "string") {
    detail = payload.uptime;
  } else if (typeof payload?.uptime === "number") {
    detail = formatDuration(payload.uptime);
  } else if (typeof payload?.detail === "string") {
    detail = payload.detail;
  }
  return { key, label, statusLabel: statusRaw || "Unknown", tone, detail };
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function formatBytes(value?: number | null): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const precision = unitIndex === 0 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(precision)} ${units[unitIndex]}`;
}

function formatUptime(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number") return formatDuration(value);
  if (value && typeof value === "object" && "human" in (value as Record<string, unknown>)) {
    return String((value as Record<string, unknown>).human);
  }
  return "—";
}

const toneClasses: Record<ServiceEntry["tone"], string> = {
  ok: "text-emerald-300 border-emerald-500/40",
  warn: "text-amber-300 border-amber-500/40",
  fail: "text-rose-300 border-rose-500/40",
};

export type MCSystemHealthProps = {
  health: MasterControlHealth;
};

export function MCSystemHealth({ health }: MCSystemHealthProps) {
  const services: ServiceEntry[] = [
    resolveServiceEntry(health, "redis", "Redis"),
    resolveServiceEntry(health, "chroma", "Chroma"),
    resolveServiceEntry(health, "postgres", "Postgres"),
    resolveServiceEntry(health, "workflow", "Workflow engine"),
    resolveServiceEntry(health, "event_stream", "Event stream"),
    resolveServiceEntry(health, "accounting", "Accounting health"),
  ];

  const statusLabel = (health.core?.status ?? "unknown").toString();
  const uptime = formatUptime(health.core?.uptime);
  const version = (health.core?.version ?? (health.core as Record<string, unknown> | null)?.release ?? null) as
    | string
    | null;
  const system = health.core?.system;
  const memory = system?.memory;
  const swap = system?.swap;
  const storage = Array.isArray(system?.storage) ? system?.storage : [];
  const vpn = system?.vpn as Record<string, unknown> | undefined;

  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">System health</CardTitle>
        <p className="text-sm text-muted-foreground">Live heartbeat from /health and accounting probes.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <div key={service.key} className="rounded-2xl border border-border bg-background/30 p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-foreground">{service.label}</p>
                <span
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide",
                    toneClasses[service.tone],
                  )}
                >
                  {service.statusLabel}
                </span>
              </div>
              {service.detail && <p className="mt-2 text-xs text-muted-foreground">{service.detail}</p>}
            </div>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">API status</p>
            <p className="mt-1 text-lg font-semibold text-foreground">{statusLabel}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Current build</p>
            <p className="mt-1 text-lg font-semibold text-foreground">{version ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Uptime</p>
            <p className="mt-1 text-lg font-semibold text-foreground">{uptime}</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-background/30 p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Memory</p>
            <p className="mt-2 text-sm text-foreground">
              {formatBytes(memory?.used_bytes)} used / {formatBytes(memory?.total_bytes)}
            </p>
            <p className="text-xs text-muted-foreground">
              {typeof memory?.percent_used === "number" ? `${memory.percent_used}% used` : "—"}
            </p>
            <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">Swap</p>
            <p className="mt-1 text-sm text-foreground">
              {formatBytes(swap?.used_bytes)} used / {formatBytes(swap?.total_bytes)}
            </p>
            <p className="text-xs text-muted-foreground">
              {typeof swap?.percent_used === "number" ? `${swap.percent_used}% used` : "—"}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-background/30 p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Storage</p>
            <div className="mt-2 space-y-2 text-sm text-foreground">
              {storage.length ? (
                storage.map((entry, idx) => (
                  <div key={`${entry.path ?? "disk"}-${idx}`} className="flex items-center justify-between gap-3">
                    <span className="text-xs text-muted-foreground">{String(entry.path ?? "disk")}</span>
                    <span>
                      {formatBytes(entry.used_bytes as number)} / {formatBytes(entry.total_bytes as number)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-muted-foreground">Storage stats unavailable.</p>
              )}
            </div>
            <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">VPN</p>
            <p className="mt-1 text-sm text-foreground">
              {(vpn?.status as string) ?? "unknown"} {vpn?.interface ? `(${vpn.interface})` : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              RX {formatBytes(vpn?.rx_bytes as number)} · TX {formatBytes(vpn?.tx_bytes as number)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default MCSystemHealth;
